<script lang="ts">
    import { t } from "$lib/i18n/translations";
    import { duoLang } from "$lib/state/duo-lang";

    import { device } from "$lib/device";
    import { hapticConfirm } from "$lib/haptics";
    import {
        copyURL,
        openURL,
        shareURL,
        openFile,
        shareFile,
    } from "$lib/download";

    import type { CobaltFileUrlType } from "$lib/types/api";

    import DialogContainer from "$components/dialog/DialogContainer.svelte";

    import Meowbalt from "$components/misc/Meowbalt.svelte";
    import DialogButtons from "$components/dialog/DialogButtons.svelte";
    import SavingTutorial from "$components/dialog/SavingTutorial.svelte";
    import VerticalActionButton from "$components/buttons/VerticalActionButton.svelte";

    import IconShare2 from "@tabler/icons-svelte/IconShare2.svelte";
    import IconDownload from "@tabler/icons-svelte/IconDownload.svelte";
    import IconFileDownload from "@tabler/icons-svelte/IconFileDownload.svelte";

    import CopyIcon from "$components/misc/CopyIcon.svelte";

    export let id: string;
    export let dismissable = true;
    export let bodyText: string = "";

    export let url: string = "";
    export let file: File | undefined = undefined;
    export let urlType: CobaltFileUrlType | undefined = undefined;

    let close: () => void;

    let copied = false;

    $: if (copied) {
        setTimeout(() => {
            copied = false;
        }, 1500);
    }
</script>

<DialogContainer {id} {dismissable} bind:close>
    <div class="dialog-body popup-body">
        <div class="meowbalt-container">
            <Meowbalt emotion="question" />
        </div>

        <div class="dialog-inner-container">
            <div class="popup-header">
                <IconFileDownload />
                <h2 class="popup-title" tabindex="-1">
                    {$t("dialog.saving.title")}
                </h2>
            </div>

            <div class="action-buttons">
                {#if device.supports.directDownload && !(device.is.iOS && urlType === "redirect")}
                    <VerticalActionButton
                        id="save-download"
                        fill
                        elevated
                        click={() => {
                            if (file) {
                                return openFile(file);
                            } else if (url) {
                                return openURL(url, true);
                            }
                        }}
                    >
                        <IconDownload />
                        {$t("button.download")}
                    </VerticalActionButton>
                {/if}

                {#if device.supports.share}
                    <VerticalActionButton
                        id="save-share"
                        fill
                        elevated
                        click={async () => {
                            if (file) {
                                return await shareFile(file);
                            } else if (url) {
                                return await shareURL(url);
                            }
                        }}
                    >
                        <IconShare2 />
                        {$t("button.share")}
                    </VerticalActionButton>
                {/if}

                {#if !file}
                    <VerticalActionButton
                        id="save-copy"
                        fill
                        elevated
                        click={async () => {
                            if (!copied) {
                                copyURL(url);
                                hapticConfirm();
                                copied = true;
                            }
                        }}
                        ariaLabel={copied ? $t("button.copied") : ""}
                    >
                        <CopyIcon check={copied} />
                        {$t("button.copy")}
                    </VerticalActionButton>
                {/if}
            </div>

            {#if device.is.iOS}
                <SavingTutorial />
            {/if}

            {#if bodyText}
                <div class="body-text">
                    {bodyText}
                </div>
            {/if}

            <!-- Bloque de Conversión y Redes Duo Creative Labs -->
            <div class="duo-post-card">
                <div class="dp-tag">DUO CREATIVE LABS · SISTEMAS & AGENTES IA</div>
                <div class="dp-title">¿Te gustó esta herramienta gratuita?</div>
                <div class="dp-desc">
                    Potencia tu negocio con <strong>agentes autónomos y flujos de IA</strong> que ahorran cientos de horas a tu equipo.
                </div>
                <div class="dp-buttons">
                    <a href="https://cal.com/duo-creative-labs/30min" target="_blank" rel="noopener noreferrer" class="dp-btn primary">
                        Agendar asesoría gratuita
                    </a>
                    <a href="https://www.instagram.com/duolabs.tech/" target="_blank" rel="noopener noreferrer" class="dp-btn secondary">
                        Síguenos en Instagram
                    </a>
                </div>
            </div>

            <!-- Descargo de Responsabilidad Legal según idioma seleccionado -->
            <div class="legal-disclaimer">
                {#if $duoLang === "en"}
                    <p class="disclaimer-text">
                        Duo Downloads is a technical utility for personal backups and public-license content. Duo Creative Labs does not host or store files on its servers and assumes no liability for third-party use of downloaded material.
                    </p>
                {:else}
                    <p class="disclaimer-text">
                        Descargas Duo es una herramienta técnica para copias de seguridad personales y contenido bajo licencias públicas. Duo Creative Labs no almacena archivos en sus servidores y no se responsabiliza del uso que terceros den al material descargado.
                    </p>
                {/if}
            </div>
        </div>

        <DialogButtons
            buttons={[
                {
                    text: $t("button.done"),
                    main: true,
                    action: () => {},
                },
            ]}
            closeFunc={close}
        />
    </div>
</DialogContainer>

<style>
    .popup-body,
    .dialog-inner-container {
        display: flex;
        flex-direction: column;
        gap: var(--padding);
    }

    .dialog-inner-container {
        overflow-y: scroll;
        gap: 8px;
        width: 100%;
    }

    .popup-body {
        max-width: 340px;
        width: calc(100% - var(--padding) - var(--dialog-padding) * 2);
        max-height: 70%;
        margin: calc(var(--padding) / 2);
    }

    .meowbalt-container {
        position: absolute;
        top: -126px;
        right: 0;
        /* simulate meowbalt being behind the popup */
        clip-path: inset(0px 0px 14px 0px);
    }

    .popup-header {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: calc(var(--padding) / 2);
        color: var(--secondary);
    }

    .popup-header :global(svg) {
        height: 21px;
        width: 21px;
    }

    .popup-title {
        color: var(--secondary);
        font-size: 19px;
    }

    .action-buttons {
        display: flex;
        flex-direction: row;
        gap: calc(var(--padding) / 2);
        position: relative;
    }

    .body-text {
        font-size: 13px;
        font-weight: 500;
        line-height: 1.5;
        color: var(--gray);
        white-space: pre-wrap;
        user-select: text;
        -webkit-user-select: text;
    }

    .duo-post-card {
        margin-top: 8px;
        padding: 11px 12px;
        background: #0d0d0d;
        border: 1px solid rgba(241, 190, 72, 0.35);
        border-radius: 10px;
        text-align: center;
    }

    :global([data-theme="light"]) .duo-post-card {
        background: #f7f7f8;
        border-color: rgba(0, 0, 0, 0.1);
    }

    .legal-disclaimer {
        margin-top: 6px;
        padding: 6px 4px 2px 4px;
        border-top: 1px solid rgba(255, 255, 255, 0.08);
        text-align: left;
    }

    :global([data-theme="light"]) .legal-disclaimer {
        border-top-color: rgba(0, 0, 0, 0.08);
    }

    .disclaimer-text {
        margin: 0 0 5px 0;
        font-size: 9.5px;
        font-style: italic;
        line-height: 1.35;
        color: var(--gray);
        opacity: 0.7;
        user-select: text;
        -webkit-user-select: text;
    }

    .disclaimer-text.en {
        margin-bottom: 0;
        opacity: 0.55;
    }

    .dp-tag {
        font-size: 9.5px;
        font-weight: 800;
        letter-spacing: 1px;
        color: #F1BE48;
        margin-bottom: 3px;
    }

    .dp-title {
        font-size: 12px;
        font-weight: 700;
        color: var(--secondary);
        margin-bottom: 4px;
    }

    .dp-desc {
        font-size: 11px;
        color: var(--gray);
        line-height: 1.35;
        margin-bottom: 8px;
    }

    .dp-desc strong {
        color: var(--secondary);
    }

    .dp-buttons {
        display: flex;
        flex-direction: column;
        gap: 5px;
    }

    .dp-btn {
        display: block;
        padding: 6px 10px;
        border-radius: 6px;
        font-size: 11px;
        font-weight: 700;
        text-decoration: none;
        transition: transform 0.15s, background 0.15s;
    }

    .dp-btn.primary {
        background: #F1BE48;
        color: #10100F;
    }

    .dp-btn.primary:hover {
        background: #ffc955;
        transform: translateY(-1px);
    }

    .dp-btn.secondary {
        background: var(--button);
        color: var(--secondary);
        border: 1px solid var(--button-stroke);
    }

    .dp-btn.secondary:hover {
        background: var(--button-hover);
        border-color: #F1BE48;
    }
</style>
